import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect } from 'react';
import { Box, Center, Flex, Spinner, Text, SimpleGrid, VStack, Field } from '@chakra-ui/react';
import { useForm, FormProvider } from 'react-hook-form';
import {
  DataTable,
  StatusBadge,
  Button as ButtonComponent,
  InputField,
  TextareaField,
  SelectInput,
  DrawerComponent,
  type Column,
  type StatusType,
  TableOptions,
} from '@themes/aegix';
import {
  useActiveEmployeeVisits,
  useTodayEmployeeVisits,
  useEmployeeReasonTypes,
  useCreateEmployeeVisit,
  useUpdateEmployeeVisit,
} from '../../hooks/queries/useVisitorManagement';
import { useUsers } from '../../hooks/queries/useUsers';
import { useSites } from '../../hooks/queries/useSites';
import { useVisitorSiteScope } from './hooks/useVisitorSiteScope';
import { useVisitorPermissions } from '../../hooks/useVisitorPermissions';

import type { EmployeeVisitListItem, VisitStatus, EmployeeVisitCreate } from '../../types/VisitorManagementTypes';
import { toaster } from '../ui/toaster';

type ViewMode = 'active' | 'today';

// STATUS_MAP built inside component

function formatTime(dateStr: string | null): string {
  if (!dateStr) return '\u2014';
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

interface EmployeeVisitRow {
  [key: string]: unknown;
  id: string;
  employee_name: string;
  status: VisitStatus;
  reason: string;
  destination: string;
  checked_in_at: string;
  checked_out_at: string | null;
}

export function EmployeeVisitContent() {
  const { t } = useTranslation();

  const STATUS_MAP = useMemo<Record<VisitStatus, { status: StatusType; label: string }>>(
    () => ({
      checked_in: { status: 'active', label: t('visitors.status.checkedIn', 'Checked In') },
      checked_out: { status: 'inactive', label: t('visitors.status.checkedOut', 'Checked Out') },
      flagged: { status: 'error', label: t('visitors.status.flagged', 'Flagged') },
      denied: { status: 'error', label: t('visitors.status.denied', 'Denied') },
    }),
    [t],
  );

  const [viewMode, setViewMode] = useState<ViewMode>('active');
  const [searchFilter, setSearchFilter] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { canParticipate, isLoading: permissionsLoading } = useVisitorPermissions();

  const { siteId, setSiteId, siteOptions } = useVisitorSiteScope();

  const { data: activeData, isLoading: activeLoading } = useActiveEmployeeVisits();
  const { data: todayData, isLoading: todayLoading } = useTodayEmployeeVisits(siteId);
  const { data: reasonTypes } = useEmployeeReasonTypes();
  const { data: usersData } = useUsers({ page_size: 1000 });
  const { data: sitesData } = useSites({ page_size: 100 });
  const createMutation = useCreateEmployeeVisit();
  const updateMutation = useUpdateEmployeeVisit();

  const methods = useForm<EmployeeVisitCreate>({
    defaultValues: {
      employee: undefined,
      school: undefined,
      reason_type: null,
      destination: '',
      notes: '',
    },
    mode: 'onBlur',
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = methods;

  useEffect(() => {
    register('school', { required: t('visitors.selectSchoolError', 'Please select a school') });
    register('employee', { required: t('visitors.selectEmployeeError', 'Please select an employee') });
    register('reason_type', { required: t('visitors.selectReasonError', 'Please select a reason') });
  }, [register, t]);



  const visits: EmployeeVisitListItem[] = useMemo(() => {
    const raw = viewMode === 'active' ? activeData : todayData;
    return Array.isArray(raw) ? raw : [];
  }, [viewMode, activeData, todayData]);

  const filteredVisits = useMemo(() => {
    if (!searchFilter) return visits;
    const q = searchFilter.toLowerCase();
    return visits.filter(
      (v) =>
        v.employee_name?.toLowerCase().includes(q) ||
        v.reason_display?.toLowerCase().includes(q) ||
        v.destination?.toLowerCase().includes(q),
    );
  }, [visits, searchFilter]);

  const rows: EmployeeVisitRow[] = filteredVisits.map((v) => ({
    id: v.public_id,
    employee_name: v.employee_name,
    status: v.status,
    reason: v.reason_display ?? '',
    destination: v.destination,
    checked_in_at: v.checked_in_at,
    checked_out_at: v.checked_out_at ?? null,
  }));

  const handleCheckOut = (publicId: string) => {
    updateMutation.mutate(
      {
        publicId,
        data: {
          status: 'checked_out',
          checked_out_at: new Date().toISOString(),
        },
      },
      {
        onSuccess: () => toaster.create({ title: t('visitors.checkedOut', 'Employee checked out'), type: 'success' }),
        onError: (err) =>
          toaster.create({
            title: t('visitors.checkOutFailed', 'Check-out failed'),
            description: err instanceof Error ? err.message : 'Unknown error',
            type: 'error',
          }),
      },
    );
  };

  const onSubmit = (values: EmployeeVisitCreate) => {
    const payload = {
      ...values,
      employee: Number(values.employee),
      reason_type:
        values.reason_type == null ? null : Number(values.reason_type),
      school: Number(values.school),
    };
    createMutation.mutate(payload, {
      onSuccess: () => {
        toaster.create({ title: t('visitors.visitCreatedSuccess', 'Employee visit created successfully'), type: 'success' });
        setDrawerOpen(false);
        reset({
          employee: undefined,
          school: undefined,
          reason_type: null,
          destination: '',
          notes: '',
        });
      },
      onError: (err) =>
        toaster.create({
          title: t('visitors.checkInFailed', 'Check-in failed'),
          description: err instanceof Error ? err.message : 'Unknown error',
          type: 'error',
        }),
    });
  };

  const columns: Column<EmployeeVisitRow>[] = useMemo(() => [
    { key: 'employee_name', label: t('visitors.employee', 'Employee'), sortable: true, width: '180px' },
    {
      key: 'status',
      label: t('common.status', 'Status'),
      width: '120px',
      render: (row) => {
        const mapped = STATUS_MAP[row.status];
        return mapped ? (
          <StatusBadge status={mapped.status} label={mapped.label} />
        ) : null;
      },
    },
    { key: 'reason', label: t('visitors.reason', 'Reason'), width: '140px' },
    { key: 'destination', label: t('visitors.destination', 'Destination'), width: '140px' },
    {
      key: 'checked_in_at',
      label: t('visitors.timeIn', 'In'),
      width: '80px',
      render: (row) => <Text fontSize="sm">{formatTime(row.checked_in_at)}</Text>,
    },
    {
      key: 'checked_out_at',
      label: t('visitors.timeOut', 'Out'),
      width: '80px',
      render: (row) => <Text fontSize="sm">{formatTime(row.checked_out_at)}</Text>,
    },
    {
      key: 'actions',
      label: '',
      width: '100px',
      render: (row) =>
        row.status === 'checked_in' && !permissionsLoading && canParticipate ? (
          <ButtonComponent
            visual="secondary"
            size="sm"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleCheckOut(row.id);
            }}
            data-e2e="employee-check-out-button"
          >
            {t('visitors.checkOut', 'Check Out')}
          </ButtonComponent>
        ) : null,
    },
  ], [t, STATUS_MAP, permissionsLoading, canParticipate]);
  const isLoading = viewMode === 'active' ? activeLoading : todayLoading;
  const activeReasonTypes = useMemo(() => {
    return (reasonTypes ?? []).filter((r) => r.is_active);
  }, [reasonTypes]);

  const activeUsers = useMemo(() => {
    return (usersData?.results ?? []).filter((u) => u.is_active !== false);
  }, [usersData]);

  const schoolOptions = useMemo(() => {
    return [...(sitesData?.results ?? [])]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((s) => ({
        label: s.name,
        value: String(s.id),
      }));
  }, [sitesData]);

  const employeeOptions = useMemo(() => {
    return activeUsers.map((u) => ({
      label: `${u.first_name} ${u.last_name} (${u.email})`,
      value: String(u.id),
    }));
  }, [activeUsers]);

  const reasonTypeOptions = useMemo(() => {
    return activeReasonTypes.map((r) => ({
      label: r.name,
      value: String(r.id),
    }));
  }, [activeReasonTypes]);
  return (
    <Box>
      <TableOptions
        toggleOptions={[
          { value: 'active', label: t('common.active', 'Active') },
          { value: 'today', label: t('common.today', 'Today') },
        ]}
        activeToggle={viewMode}
        onToggleChange={(val) => setViewMode(val as ViewMode)}
        searchValue={searchFilter}
        onSearchChange={setSearchFilter}
        primaryAction={
          !permissionsLoading && canParticipate
            ? {
                label: t('visitors.checkInEmployee', 'Check In Employee'),
                onClick: () => setDrawerOpen(true),
              }
            : undefined
        }
      />

      <Box px={5} pb={5}>
        {viewMode === 'today' && siteOptions.length > 1 && (
          <Flex justify="flex-end" pb={3}>
            <SelectInput
              value={siteId == null ? '' : String(siteId)}
              name="employee-today-site"
              label={t('common.school', 'School')}
              option={siteOptions}
              onChange={(val: string) => setSiteId(Number(val))}
              e2eTitle="employee-today-site-select"
            />
          </Flex>
        )}
        {isLoading ? (
          <Center py={12}>
            <Spinner size="lg" color="button.primary" />
          </Center>
        ) : rows.length === 0 ? (
          <Center py={12}>
            <Text color="text.secondary">
              {searchFilter
                ? t('visitors.noMatchSearch', 'No employee visits match your search')
                : viewMode === 'active'
                  ? t('visitors.noActiveEmployeeVisits', 'No active employee visits right now')
                  : t('visitors.noEmployeeVisitsToday', 'No employee visits recorded today')}
            </Text>
          </Center>
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            keyExtractor={(row) => row.id}
          />
        )}
      </Box>

      <DrawerComponent
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('visitors.checkInEmployee', 'Check In Employee')}
        size="md"
        footer={
          <SimpleGrid columns={2} gap={4} width="100%">
            <ButtonComponent
              visual="secondary"
              type="button"
              onClick={() => setDrawerOpen(false)}
              data-e2e="employee-checkin-cancel"
            >
              {t('common.cancel', 'Cancel')}
            </ButtonComponent>
            <ButtonComponent
              visual="primary"
              type="submit"
              form="employee-checkin-form"
              data-e2e="employee-checkin-submit"
            >
              {t('visitors.checkIn', 'Check In')}
            </ButtonComponent>
          </SimpleGrid>
        }
      >
        <FormProvider {...methods}>
          <form id="employee-checkin-form" onSubmit={handleSubmit(onSubmit)}>
            <VStack gap={4} align="stretch">
              <Field.Root invalid={!!errors.school}>
                <SelectInput
                  value={watch('school') == null ? '' : String(watch('school'))}
                  name="school"
                  label={t('common.school', 'School')}
                  option={schoolOptions}
                  onChange={(val: string) =>
                    setValue('school', Number(val), { shouldTouch: true, shouldValidate: true })
                  }
                  e2eTitle="employee-school-select"
                  aria-invalid={!!errors.school}
                  aria-describedby={errors.school ? 'checkin-school-error' : undefined}
                />
                {errors.school && (
                  <Field.ErrorText id="checkin-school-error" data-e2e="employee-school-error">
                    {errors.school.message ? t(errors.school.message) : null}
                  </Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={!!errors.employee}>
                <SelectInput
                  value={watch('employee') == null ? '' : String(watch('employee'))}
                  name="employee"
                  label={t('visitors.employee', 'Employee')}
                  option={employeeOptions}
                  onChange={(val: string) =>
                    setValue('employee', Number(val), { shouldTouch: true, shouldValidate: true })
                  }
                  e2eTitle="employee-select"
                  aria-invalid={!!errors.employee}
                  aria-describedby={errors.employee ? 'checkin-employee-error' : undefined}
                />
                {errors.employee && (
                  <Field.ErrorText id="checkin-employee-error" data-e2e="employee-select-error">
                    {errors.employee.message ? t(errors.employee.message) : null}
                  </Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={!!errors.reason_type}>
                <SelectInput
                  value={watch('reason_type') == null ? '' : String(watch('reason_type'))}
                  name="reason_type"
                  label={t('visitors.reasonType', 'Reason Type')}
                  option={reasonTypeOptions}
                  onChange={(val: string) =>
                    setValue('reason_type', val === '' ? null : Number(val))
                  }
                  e2eTitle="employee-reason-select"
                  aria-invalid={!!errors.reason_type}
                  aria-describedby={errors.reason_type ? 'checkin-reason-error' : undefined}
                />
                {errors.reason_type && (
                  <Field.ErrorText id="checkin-reason-error" data-e2e="employee-reason-error">
                    {errors.reason_type.message ? t(errors.reason_type.message) : null}
                  </Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={!!errors.destination}>
                <Field.Label htmlFor="checkin-destination">{t('visitors.destination', 'Destination')}</Field.Label>
                <InputField
                  id="checkin-destination"
                  {...register('destination', { required: t('visitors.enterDestination', 'Please enter a destination') })}
                  placeholder={t('visitors.destinationPlaceholder', 'e.g. Science Lab, Room 204')}
                  data-e2e="employee-destination-input"
                  aria-invalid={!!errors.destination}
                  aria-describedby={errors.destination ? 'checkin-destination-error' : undefined}
                />
                {errors.destination && (
                  <Field.ErrorText id="checkin-destination-error" data-e2e="employee-destination-error">
                    {errors.destination.message ? t(errors.destination.message) : null}
                  </Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={!!errors.notes}>
                <Field.Label htmlFor="checkin-notes">{t('common.notes', 'Notes')}</Field.Label>
                <TextareaField
                  id="checkin-notes"
                  {...register('notes')}
                  placeholder={t('visitors.notesPlaceholder', 'Optional visit details or supervisor approval reference...')}
                  rows={3}
                  data-e2e="employee-notes-textarea"
                  aria-invalid={!!errors.notes}
                  aria-describedby={errors.notes ? 'checkin-notes-error' : undefined}
                />
                {errors.notes && (
                  <Field.ErrorText id="checkin-notes-error" data-e2e="employee-notes-error">
                    {errors.notes.message ? t(errors.notes.message) : null}
                  </Field.ErrorText>
                )}
              </Field.Root>
            </VStack>
          </form>
        </FormProvider>
      </DrawerComponent>
    </Box>
  );
}

// -----------------------------------------------------------------------------
// Accessible search filter input wrapped in Field.Root
// -----------------------------------------------------------------------------
interface InputFilterProps {
  placeholder: string;
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
}

function InputFilter({ placeholder, value, onChange }: InputFilterProps) {
  return (
    <Field.Root>
      <Field.Label htmlFor="filter-employee-visits" srOnly>
        {placeholder}
      </Field.Label>
      <InputField
        id="filter-employee-visits"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        maxW="250px"
        size="sm"
        data-e2e="filter-employee-visits-input"
      />
    </Field.Root>
  );
}
